import { useMemo } from "react"
import { useAuthStore } from "@/stores/auth"
import {
  hasAnyUserType,
  hasUserType,
  normalizeUserTypes,
} from "@/constants/user-types"

const OWNER_TYPES = ["WOEZOR_BUSINESS_OWNER"] as const
const BUSINESS_ADMIN_TYPES = ["WOEZOR_BUSINESS_ADMIN"] as const
const PROVIDER_TYPES = ["WOEZOR_BUSINESS_SERVICE_PROVIDER"] as const
const PLATFORM_STAFF_TYPES = [
  "WOEZOR_SUPER",
  "WOEZOR_ADMIN",
  "WOEZOR_SUPPORT",
] as const

export function useTenantCapabilities() {
  const user = useAuthStore((state) => state.user)

  return useMemo(() => {
    const types = normalizeUserTypes(user?.userType)
    const isOwner = hasAnyUserType(user, OWNER_TYPES)
    const isBusinessAdmin = hasAnyUserType(user, BUSINESS_ADMIN_TYPES)
    const isProvider = hasAnyUserType(user, PROVIDER_TYPES)
    const isPlatformStaff = hasAnyUserType(user, PLATFORM_STAFF_TYPES)

    const isProviderOnly =
      isProvider && !isOwner && !isBusinessAdmin && !isPlatformStaff

    const canManageTenant =
      isOwner || isBusinessAdmin || isPlatformStaff

    return {
      types,
      isOwner,
      isBusinessAdmin,
      isProvider,
      isProviderOnly,
      isPlatformStaff,
      canManageTenant,
      canAccessFinance: canManageTenant,
      canManageProviders: canManageTenant,
      canManageCustomers: canManageTenant,
      canManageBusinesses: canManageTenant,
      canImportBusiness: isOwner || isBusinessAdmin || isPlatformStaff,
      canManageCampaigns:
        isOwner ||
        isBusinessAdmin ||
        isPlatformStaff,
      canManageServices: canManageTenant || isProvider,
      /** Provider-only users: appointments + settings focus */
      canAccessNavPath: (path: string) => {
        if (!isProviderOnly) return true
        const allowedPrefixes = [
          "/dashboard",
          "/appointments",
          "/slot-requests",
          "/settings",
        ]
        return allowedPrefixes.some(
          (prefix) => path === prefix || path.startsWith(`${prefix}/`),
        )
      },
    }
  }, [user])
}

export function useIsBusinessOwner() {
  const user = useAuthStore((state) => state.user)
  return hasUserType(user, "WOEZOR_BUSINESS_OWNER")
}
